/**
 * Persist a custom service name added on-the-fly from a job modal.
 *
 * Background: Owners often type one-off service names ("Pressure Wash
 * Patio", "Roof Moss Treatment") via the "+ Add new (type your own)…"
 * prompt in the Add/Edit Job modals. Without this helper, the typed
 * service stays on that single job only — next time the dropdown
 * rebuilds from `/api/services`, the name is gone and the owner has
 * to retype it or visit Settings → Services to save it manually.
 *
 * This helper:
 *   1. Skips empty / whitespace-only inputs.
 *   2. Skips names already in the local `existingServices` list (case
 *      insensitive) so we don't create duplicate Service records.
 *   3. POSTs to `/api/services` with just the name (price/duration are
 *      optional — the owner can fill them later in Settings).
 *   4. Returns the new Service object on success, or `null` on
 *      failure / duplicate / empty input. Callers can splice it into
 *      their local `services` state so it appears in subsequent
 *      dropdown renders without a full refetch.
 *
 * Failures are intentionally silent (console.warn only) — the job save
 * itself isn't blocked. Worst-case the dropdown won't be updated and
 * the owner can re-type next time.
 */
const API_URL = process.env.REACT_APP_BACKEND_URL;

export async function persistCustomService(name, existingServices = []) {
  const trimmed = (name || '').trim();
  if (!trimmed) return null;

  // De-dupe (case-insensitive). The backend has a uniqueness check too,
  // but doing it client-side avoids a needless round-trip + 400 response.
  const lower = trimmed.toLowerCase();
  if (existingServices.some(s => (s.name || s.service || '').toLowerCase() === lower)) {
    return null;
  }

  try {
    const token = localStorage.getItem('streakfree_token');
    if (!token) return null;
    const res = await fetch(`${API_URL}/api/services`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        name: trimmed,
        // Price/duration left null so the owner is reminded to fill them
        // in next time they edit it from Settings. Defaults aren't useful
        // since custom services tend to be priced per-job anyway.
        default_price: null,
        default_duration: 60,
        description: null,
      }),
    });
    if (!res.ok) {
      console.warn('Failed to persist custom service:', await res.text());
      return null;
    }
    return await res.json();
  } catch (err) {
    console.warn('persistCustomService network error:', err);
    return null;
  }
}
